// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { formatTime } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { getID, shortenSizeUnit } from '../../utils';
import { formatNumber } from '../../utils/formatNumber';
import { VisibleAtContainer } from '../common/containers/VisibleAtContainer';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import { CropIdLink } from '../crop';
import HarvestLink from '../harvest/HarvestLink';
import { PlantingGroupIdLink } from '../plantingGroup';

export interface SurplusGroup {
  readonly harvestTime: number;
  readonly expirationTime: number;
  readonly plantingId: string;
  readonly cropData: {
    readonly id: string;
    readonly crop_name: string;
    readonly yield_unit: string;
  };
  readonly surplusTrays: number;
  readonly surplusAmount: number;
}

interface Props {
  readonly surpluses: readonly SurplusGroup[];
}

export default function SurplusTable({ surpluses }: Props) {
  const { timezone, language } = useUserContext();
  const plantingValues: React.ReactElement[] = [];
  const cropLinks: React.ReactElement[] = [];
  const harvestTimes: React.ReactElement[] = [];
  const expirationTimeValues: React.ReactElement[] = [];
  const surplusTrayCounts: string[] = [];
  const surplusAmounts: string[] = [];

  for (const surplus of surpluses) {
    const crop = surplus.cropData;
    plantingValues.push(
      <VisibleAtContainer
        up={<PlantingGroupIdLink id={surplus.plantingId} />}
        down={<PlantingGroupIdLink id={surplus.plantingId} title={surplus.plantingId} />}
      />,
    );
    cropLinks.push(<CropIdLink id={getID(crop.id)} name={crop.crop_name} />);
    harvestTimes.push(
      <VisibleAtContainer
        up={<HarvestLink id={surplus.plantingId} title={formatTime(surplus.harvestTime, { timezone })} />}
        down={
          <HarvestLink
            id={surplus.plantingId}
            title={formatTime(surplus.harvestTime, { timezone, compactDisplay: true, removeDayOfWeek: true })}
          />
        }
      />,
    );
    expirationTimeValues.push(
      <VisibleAtContainer
        up={formatTime(surplus.expirationTime, { timezone })}
        down={formatTime(surplus.expirationTime, { timezone, compactDisplay: true, removeDayOfWeek: true })}
      />,
    );
    surplusTrayCounts.push(formatNumber(surplus.surplusTrays, language));
    surplusAmounts.push(
      `${formatNumber(surplus.surplusAmount, language)} ${shortenSizeUnit(surplus.cropData.yield_unit)}`,
    );
  }
  const columns: ColumnType[] = [
    {
      name: 'Planting',
      values: plantingValues,
    },
    {
      name: 'Crop',
      values: cropLinks,
    },
    {
      name: 'Harvest Date',
      smallName: 'Harvest',
      values: harvestTimes,
      visibleAt: 'lg',
    },
    {
      name: 'Expiration Date',
      smallName: 'Expires',
      values: expirationTimeValues,
    },
    {
      name: 'Surplus Trays',
      values: surplusTrayCounts,
      visibleAt: 'md',
      numeric: true,
    },
    {
      name: 'Surplus',
      values: surplusAmounts,
    },
  ];

  return <Table columns={columns} />;
}
